import { Link } from 'react-router-dom';
import {
  ArrowRightCircle,
  ArrowLeftCircle,
  ArrowRightLeft,
  Clock,
  MapPin,
  User,
  AlertTriangle,
  CheckCircle,
  XCircle,
  HelpCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { cn } from '@/app/components/ui/utils';
import type { DeploymentWithDetails } from '@/hooks/use-deployments';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { ApprovalStatus, DeploymentAction } from '@/lib/types';

interface DeploymentCardProps {
  deployment: DeploymentWithDetails;
  onApprove?: (id: number) => void;
  onReject?: (id: number) => void;
  showActions?: boolean;
  isApproving?: boolean;
  isRejecting?: boolean;
}

const actionIcons: Record<DeploymentAction, React.ReactNode> = {
  check_out: <ArrowRightCircle className="h-5 w-5" />,
  check_in: <ArrowLeftCircle className="h-5 w-5" />,
  transfer: <ArrowRightLeft className="h-5 w-5" />,
};

const actionLabels: Record<DeploymentAction, string> = {
  check_out: 'Check Out',
  check_in: 'Check In',
  transfer: 'Transfer',
};

const actionColors: Record<DeploymentAction, string> = {
  check_out: 'text-blue-600 bg-blue-50',
  check_in: 'text-green-600 bg-green-50',
  transfer: 'text-purple-600 bg-purple-50',
};

const approvalStatusConfig: Record<
  ApprovalStatus,
  { label: string; icon: React.ReactNode; className: string }
> = {
  pending: {
    label: 'Pending',
    icon: <HelpCircle className="h-3 w-3" />,
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  approved: {
    label: 'Approved',
    icon: <CheckCircle className="h-3 w-3" />,
    className: 'bg-green-100 text-green-800 border-green-200',
  },
  rejected: {
    label: 'Rejected',
    icon: <XCircle className="h-3 w-3" />,
    className: 'bg-red-100 text-red-800 border-red-200',
  },
};

export function DeploymentCard({
  deployment,
  onApprove,
  onReject,
  showActions = false,
  isApproving = false,
  isRejecting = false,
}: DeploymentCardProps) {
  const orgTimeZone = useOrgTimeZone();
  const isOverdue = deployment.overdue;
  const approvalConfig = approvalStatusConfig[deployment.approval_status];

  return (
    <Card
      className={cn(
        'transition-colors',
        isOverdue && 'border-red-300 bg-red-50/50'
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div
              className={cn(
                'p-2 rounded-lg shrink-0',
                actionColors[deployment.action]
              )}
            >
              {actionIcons[deployment.action]}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900">
                  {actionLabels[deployment.action]}
                </span>
                <Badge
                  variant="outline"
                  className={cn('text-xs', approvalConfig.className)}
                >
                  <span className="mr-1">{approvalConfig.icon}</span>
                  {approvalConfig.label}
                </Badge>
                {isOverdue && (
                  <Badge variant="destructive" className="text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Overdue
                  </Badge>
                )}
              </div>

              <div className="mt-1">
                <Link
                  to={`/assets/${deployment.asset}`}
                  className="text-sm text-blue-600 hover:underline font-medium"
                >
                  {deployment.asset_name || `Asset #${deployment.asset}`}
                </Link>
                {deployment.asset_serial_number && (
                  <span className="text-sm text-gray-500 ml-2">
                    ({deployment.asset_serial_number})
                  </span>
                )}
              </div>

              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span>
                    {formatDateTimeInTimeZone(deployment.timestamp, orgTimeZone, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                </div>

                {deployment.assigned_to_user_name && (
                  <div className="flex items-center gap-1">
                    <User className="h-4 w-4 text-gray-400" />
                    <span>{deployment.assigned_to_user_name}</span>
                  </div>
                )}

                {deployment.location_name && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span>{deployment.location_name}</span>
                  </div>
                )}

                {deployment.due_date && (
                  <div
                    className={cn(
                      'flex items-center gap-1',
                      isOverdue && 'text-red-600 font-medium'
                    )}
                  >
                    <AlertTriangle className="h-4 w-4" />
                    <span>
                      Due:{' '}
                      {formatDateOnly(deployment.due_date, '—', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                )}
              </div>

              {deployment.notes && (
                <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                  {deployment.notes}
                </p>
              )}
            </div>
          </div>

          {showActions && deployment.approval_status === 'pending' && (
            <div className="flex gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                className="text-green-600 border-green-300 hover:bg-green-50"
                onClick={() => onApprove?.(deployment.id)}
                disabled={isApproving || isRejecting}
              >
                {isApproving ? 'Approving...' : 'Approve'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => onReject?.(deployment.id)}
                disabled={isApproving || isRejecting}
              >
                {isRejecting ? 'Rejecting...' : 'Reject'}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
